<template>
  <div class="form-wizard-step">
    <div class="form-wizard-content text-center">
      <div class="container">
        <RoofTypeSelect
          :roof-types="roofTypes"
          :current="roofType"
          @on-select="setRoofType"
          :description="description"
        />
        <ServerValidation field="roof_type" :server-validation="serverValidation" />

        <ServerValidation field="degree" :server-validation="serverValidation" />
      </div>
    </div>

    <div class="form-wizard-footer">
      <div class="form-wizard-actions container">
        <div class="row">
          <div class="col-md-6 offset-md-3">
            <button
              data-test="next-step"
              @click="validateStep"
              :disabled="isLastStep || isLoading"
              class="btn btn-primary btn-block"
            >
              {{ !isLoading ? t('steps.roofType.buttons.next') : t('general.loading') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onBeforeMount, reactive, toRefs, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useVuelidate } from '@vuelidate/core'
import { useI18n } from 'vue-i18n'

import httpClient from '@/services/api/httpClient'
import useFormWizard from '@/hooks/formWizard'
import useFormState from '@/hooks/formState'
import useFormControl from '@/hooks/formControl'

import RoofTypeSelect from '@/components/Calculator/RoofTypeSelect.vue'
import ServerValidation from '@/components/Global/ServerValidation.vue'

export default defineComponent({
  name: 'RoofType',
  components: {
    RoofTypeSelect,
    ServerValidation
  },
  setup() {
    const { nextStep, isLastStep } = useFormWizard()
    const { updateFormState, getFieldValue } = useFormState()
    const { serverValidation } = useFormControl()
    const toast = useToast()
    const { t } = useI18n()

    const roofTypes = ref([])
    const isLoading = ref(false)

    const state = reactive({
      roofType: getFieldValue('roofType') || false,
      description: ''
    })

    const validations = useVuelidate()

    onBeforeMount(() => {
      fetchStepData()
      document.addEventListener('keypress', submitHandler)
    })

    onUnmounted(() => {
      document.removeEventListener('keypress', submitHandler)
    })

    const submitHandler = e => e.key === 'Enter' && validateStep()

    const fetchStepData = async () => {
      try {
        isLoading.value = true

        const { data } = await httpClient.get('roof-types')

        roofTypes.value = data.data.roof_types
        state.description = data.data.description

        if (!getFieldValue('roofType') && roofTypes.value.length) {
          setRoofType(roofTypes.value[0])
        }
      } catch (error) {
        toast.error(t('errors.data'))
      } finally {
        isLoading.value = false
      }
    }

    const setRoofType = roofType => {
      state.roofType = roofType
    }

    const validateStep = async () => {
      if (isLoading.value) return

      validations.value.$touch()

      if (validations.value.$invalid) return

      serverValidation.value = {}

      try {
        isLoading.value = true

        const data = {
          roof_type: state.roofType.id
        }

        await httpClient.post('roof-types', data)

        updateFormState({ ...state })

        nextStep({ label: state.roofType.title })

        return Promise.resolve()
      } catch (error) {
        if (error.errStatus === 422 || error.errStatus === 404) {
          serverValidation.value = Object.assign({}, error.errData.data.params)
        } else {
          toast.error(t('errors.save'))
        }
      } finally {
        isLoading.value = false
      }
    }

    return {
      nextStep,
      isLastStep,
      roofTypes,
      ...toRefs(state),
      setRoofType,
      validateStep,
      isLoading,
      serverValidation,
      t
    }
  }
})
</script>
